'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import ChannelBadge from '@/components/ui/ChannelBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import PriorityBadge from '@/components/ui/PriorityBadge';
import type { CallState } from './ActiveInteractionContent';

const timeline = [
  { id: 'tl-001', date: 'Today', channel: 'PHONE' as const, topic: 'MySejahtera Mobile Number Update', status: 'In Progress', time: '14:32', agency: 'KKM', ticket: 'MYGCC-TKT-2026-010482', interactionId: 'INT-2026-14892' },
  { id: 'tl-002', date: 'Yesterday', channel: 'WHATSAPP' as const, topic: 'Passport Renewal Documentation', status: 'Resolved', time: '09:14', agency: 'JIM', ticket: 'MYGCC-TKT-2026-010211', interactionId: 'INT-2026-14880' },
  { id: 'tl-003', date: '2 days ago', channel: 'LIVE CHAT' as const, topic: 'Road Tax Enquiry', status: 'Resolved', time: '11:45', agency: 'JPJ', ticket: 'MYGCC-TKT-2026-009981', interactionId: 'INT-2026-14871' },
  { id: 'tl-004', date: '3 weeks ago', channel: 'EMAIL' as const, topic: 'Government Assistance Enquiry', status: 'Closed', time: '08:20', agency: 'KPKT', ticket: 'MYGCC-TKT-2026-009742', interactionId: 'INT-2026-14820' },
  { id: 'tl-005', date: '5 weeks ago', channel: 'PHONE' as const, topic: 'Road Tax Renewal Inquiry', status: 'Resolved', time: '15:02', agency: 'JPJ', ticket: null, interactionId: 'INT-2026-14750' },
];

const cases = [
  { id: 'MYGCC-2026-008432', title: 'MySejahtera Mobile Number Update', agency: 'Kementerian Kesihatan Malaysia (KKM)', status: 'New', priority: 'Medium' as const, created: 'Today', updated: 'Today' },
  { id: 'MYGCC-2026-008420', title: 'MySejahtera Account Update', agency: 'Kementerian Kesihatan Malaysia (KKM)', status: 'Open', priority: 'Medium' as const, created: 'Yesterday', updated: 'Yesterday' },
  { id: 'MYGCC-2026-008311', title: 'Government Assistance Enquiry', agency: 'KPKT', status: 'Closed', priority: 'Low' as const, created: '3 weeks ago', updated: '3 weeks ago' },
];

const tickets = [
  { id: 'MYGCC-TKT-2026-010482', title: 'MySejahtera Mobile Number Update', status: 'Open', agency: 'KKM' },
  { id: 'MYGCC-TKT-2026-010211', title: 'Passport Renewal Documentation', status: 'Resolved', agency: 'JIM' },
  { id: 'MYGCC-TKT-2026-009981', title: 'Road Tax Enquiry', status: 'Resolved', agency: 'JPJ' },
  { id: 'MYGCC-TKT-2026-009742', title: 'Government Assistance Application', status: 'Closed', agency: 'KPKT' },
];

interface CaseDetailProps {
  caseItem: typeof cases[0];
  onBack: () => void;
}

function CaseDetail({ caseItem, onBack }: CaseDetailProps) {
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[12px] text-primary font-600 hover:underline">
          <Icon name="ArrowLeftIcon" size={13} />
          Back to Cases
        </button>
      </div>
      <div className="card-elevated p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[12px] font-700 text-primary mb-0.5">{caseItem.id}</div>
            <div className="text-[15px] font-700 text-foreground">{caseItem.title}</div>
          </div>
          <PriorityBadge priority={caseItem.priority} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-[12px] mb-3">
          {[
            { label: 'Customer', value: 'Ahmed Firdaus' },
            { label: 'Agency', value: caseItem.agency },
            { label: 'Status', value: caseItem.status },
            { label: 'Created', value: caseItem.created },
            { label: 'Last Updated', value: caseItem.updated },
            { label: 'Assigned Officer', value: 'Nur Aisyah' },
          ].map(f => (
            <div key={f.label}>
              <div className="text-muted-foreground text-[10px]">{f.label}</div>
              <div className="font-600 text-foreground">{f.value}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-3">
          <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-1.5">Description</div>
          <div className="text-[12px] text-foreground leading-relaxed">
            Citizen contacted MyGCC regarding {caseItem.title.toLowerCase()}. Issue was investigated and appropriate guidance was provided.
          </div>
        </div>
      </div>
      <div className="card-elevated p-3">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Linked Tickets</div>
        {tickets.slice(0, 2).map(t => (
          <div key={t.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
            <div>
              <div className="text-[12px] font-600 text-primary">{t.id}</div>
              <div className="text-[11px] text-muted-foreground">{t.title}</div>
            </div>
            <StatusBadge status={t.status} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={() => toast.success('Case updated')} className="flex-1 btn-primary py-2 text-[12px]">Update Case</button>
        <button onClick={() => toast.info('Interaction linked')} className="flex-1 btn-secondary py-2 text-[12px]">Link Interaction</button>
      </div>
    </div>
  );
}

interface TicketDetailProps {
  ticket: typeof tickets[0];
  onBack: () => void;
}

function TicketDetail({ ticket, onBack }: TicketDetailProps) {
  return (
    <div className="p-4 space-y-4">
      <button onClick={onBack} className="flex items-center gap-1.5 text-[12px] text-primary font-600 hover:underline">
        <Icon name="ArrowLeftIcon" size={13} />
        Back to Timeline
      </button>
      <div className="card-elevated p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[12px] font-700 text-primary mb-0.5">{ticket.id}</div>
            <div className="text-[15px] font-700 text-foreground">{ticket.title}</div>
          </div>
          <StatusBadge status={ticket.status} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-[12px] mb-3">
          {[
            { label: 'Customer', value: 'Ahmed Firdaus' },
            { label: 'Agency', value: ticket.agency },
            { label: 'Channel', value: 'Phone' },
            { label: 'Priority', value: 'Medium' },
            { label: 'Created', value: '16 Aug 2026' },
            { label: 'Last Updated', value: '16 Aug 2026' },
          ].map(f => (
            <div key={f.label}>
              <div className="text-muted-foreground text-[10px]">{f.label}</div>
              <div className="font-600 text-foreground">{f.value}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-3 space-y-2">
          <div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-1">Ticket Description</div>
            <div className="text-[12px] text-foreground leading-relaxed">
              Citizen requested assistance with {ticket.title.toLowerCase()}. Agent provided guidance based on approved knowledge base articles.
            </div>
          </div>
          <div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-1">AI Summary</div>
            <div className="flex items-start gap-1.5 bg-ai-bg border border-ai-border rounded-lg p-2">
              <Icon name="SparklesIcon" size={11} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-[11px] text-foreground">
                <span className="text-[9px] text-primary font-700 bg-primary/10 px-1 py-0.5 rounded mr-1">AI Generated · Agent Reviewed</span>
                Citizen contacted regarding {ticket.title.toLowerCase()}. Issue resolved at first contact.
              </div>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-1">MIR Recording</div>
            <div className="text-[11px] text-muted-foreground font-mono">MIR-20260816-14892</div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onBack} className="flex-1 btn-secondary py-2 text-[12px]">Back to Interaction</button>
        <button onClick={() => toast.info('Ticket opened in Cases')} className="flex-1 btn-primary py-2 text-[12px]">Open in Cases</button>
      </div>
    </div>
  );
}

interface Props {
  callState: CallState;
  caseCreated: boolean;
}

export default function Customer360Column({ callState, caseCreated }: Props) {
  const [activeSection, setActiveSection] = useState<'overview' | 'timeline' | 'cases' | 'tickets'>('overview');
  const [selectedCase, setSelectedCase] = useState<typeof cases[0] | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<typeof tickets[0] | null>(null);

  if (selectedCase) {
    return <CaseDetail caseItem={selectedCase} onBack={() => setSelectedCase(null)} />;
  }

  if (selectedTicket) {
    return <TicketDetail ticket={selectedTicket} onBack={() => setSelectedTicket(null)} />;
  }

  return (
    <div className="p-4 space-y-4">
      {/* C360 Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="UserCircleIcon" size={15} className="text-primary" />
          <h2 className="text-[13px] font-700">Customer 360</h2>
        </div>
        <span className="text-[10px] bg-green-50 text-green-600 border border-green-200 px-2 py-0.5 rounded font-600 flex items-center gap-1">
          <Icon name="CheckCircleIcon" size={10} />
          Citizen Identified
        </span>
      </div>

      {/* Previous Context Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-2.5 flex items-start gap-2">
        <Icon name="InformationCircleIcon" size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-[11px] text-blue-800">
          <span className="font-700">Previous Interaction Context:</span> Citizen asked about passport renewal documentation via WhatsApp yesterday.
          <button onClick={() => toast.info('Loading previous WhatsApp context — Passport Renewal Documentation, 12 Aug 2026')} className="ml-1.5 text-blue-600 font-600 underline hover:no-underline">View Context</button>
        </div>
      </div>

      {/* Citizen Profile */}
      <div className="card-elevated p-3">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white text-lg font-700 flex-shrink-0">AF</div>
          <div>
            <div className="text-[15px] font-700 text-foreground">Ahmed Firdaus</div>
            <div className="text-[12px] text-muted-foreground">Existing Citizen Contact</div>
            <div className="text-[11px] text-muted-foreground">IC: ****-**-4821 · Selangor</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Total Interactions', value: '9', icon: 'ChatBubbleLeftRightIcon' },
            { label: 'Open Cases', value: '2', icon: 'FolderOpenIcon', alert: true },
            { label: 'Closed Cases', value: '5', icon: 'ArchiveBoxIcon' },
            { label: 'Last Contact', value: '12 Aug', icon: 'CalendarIcon' },
          ].map((s) => (
            <div key={`c360stat-${s.label}`} className="text-center bg-muted/30 rounded-lg p-2">
              <div className={`text-[16px] font-700 font-tabular ${s.alert ? 'text-amber-600' : 'text-foreground'}`}>{s.value}</div>
              <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-border text-[11px]">
          <span className="text-muted-foreground">Preferred Channel:</span>
          <ChannelBadge channel="WHATSAPP" />
          <span className="text-muted-foreground ml-auto">Language: Bahasa Melayu</span>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-1 border-b border-border">
        {(['overview', 'timeline', 'cases', 'tickets'] as const).map((s) => (
          <button
            key={`sect-${s}`}
            onClick={() => setActiveSection(s)}
            className={`px-3 py-1.5 text-[12px] font-600 capitalize border-b-2 transition-all ${
              activeSection === s ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {s === 'overview' ? 'Overview' : s === 'timeline' ? 'Timeline' : s === 'cases' ? 'Cases' : 'Tickets'}
          </button>
        ))}
      </div>

      {activeSection === 'overview' && (
        <div className="space-y-3 fade-in-up">
          <div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Agency Engagement</div>
            <div className="space-y-1.5">
              {[
                { agency: 'Kementerian Kesihatan Malaysia', count: 4, pct: 45 },
                { agency: 'Jabatan Imigresen Malaysia', count: 2, pct: 22 },
                { agency: 'Jabatan Pengangkutan Jalan', count: 2, pct: 22 },
                { agency: 'Jabatan Pendaftaran Negara', count: 1, pct: 11 },
              ].map((a) => (
                <div key={`agency-${a.agency}`} className="flex items-center gap-2 text-[12px]">
                  <span className="text-muted-foreground w-44 truncate">{a.agency}</span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${a.pct}%` }} />
                  </div>
                  <span className="font-600 w-4 text-right">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Recent Topics</div>
            <div className="flex flex-wrap gap-1.5">
              {['MySejahtera', 'Passport Renewal', 'Road Tax', 'Government Assistance', 'Account Update'].map((t) => (
                <span key={`topic-${t}`} className="text-[11px] bg-secondary text-primary px-2 py-0.5 rounded font-500">{t}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSection === 'timeline' && (
        <div className="space-y-0 fade-in-up">
          {timeline.map((item, i) => (
            <div key={item.id} className="flex gap-3 pb-3">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-primary' : 'bg-muted'}`}>
                  <ChannelBadge channel={item.channel} showLabel={false} />
                </div>
                {i < timeline.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[12px] font-700 text-foreground">{item.date}</span>
                  <span className="text-[11px] text-muted-foreground">{item.time}</span>
                </div>
                <button
                  onClick={() => toast.info(`Loading interaction: ${item.topic}`)}
                  className="text-[12px] text-foreground mb-1 hover:text-primary transition-colors text-left"
                >
                  {item.topic}
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <ChannelBadge channel={item.channel} />
                  <StatusBadge status={item.status} />
                  <span className="text-[11px] text-muted-foreground">{item.agency}</span>
                  {item.ticket && (
                    <button
                      onClick={() => setSelectedTicket(tickets.find(t => t.id === item.ticket) || null)}
                      className="text-[10px] text-primary font-600 hover:underline"
                    >
                      {item.ticket}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {caseCreated && (
            <div className="flex gap-3 pb-3 fade-in-up">
              <div className="flex flex-col items-center">
                <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <Icon name="CheckIcon" size={12} className="text-white" />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[12px] font-700 text-green-600">Today — Case Created</span>
                  <span className="text-[11px] text-muted-foreground">Just now</span>
                </div>
                <div className="text-[12px] text-foreground">MYGCC-2026-008431 · Resolved at First Contact</div>
                <StatusBadge status="Resolved" />
              </div>
            </div>
          )}
        </div>
      )}

      {activeSection === 'cases' && (
        <div className="space-y-2.5 fade-in-up">
          {cases.map((c) => (
            <div key={c.id} className="card-elevated p-3 hover:border-primary/30 transition-colors cursor-pointer" onClick={() => setSelectedCase(c)}>
              <div className="flex items-start justify-between mb-1.5">
                <div>
                  <div className="text-[12px] font-700 text-primary">{c.id}</div>
                  <div className="text-[13px] font-600 text-foreground mt-0.5">{c.title}</div>
                </div>
                <PriorityBadge priority={c.priority} />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <StatusBadge status={c.status} />
                <span className="text-[11px] text-muted-foreground">{c.agency}</span>
              </div>
              <div className="flex gap-1.5">
                <button onClick={(e) => { e.stopPropagation(); setSelectedCase(c); }} className="btn-secondary px-2.5 py-1 text-[11px]">Open Case</button>
                <button onClick={(e) => { e.stopPropagation(); toast.success(`Interaction linked to ${c.id}`); }} className="btn-ghost px-2.5 py-1 text-[11px] border border-border">
                  Link Interaction
                </button>
              </div>
            </div>
          ))}
          <button onClick={() => toast.info('New case creation — AI draft will be available after call')} className="w-full btn-secondary py-2 text-[12px] flex items-center justify-center gap-1.5">
            <Icon name="PlusIcon" size={13} />
            Create New Case
          </button>
        </div>
      )}

      {activeSection === 'tickets' && (
        <div className="space-y-2 fade-in-up">
          {tickets.map((t) => (
            <div key={t.id} className="card-elevated p-3 hover:border-primary/30 transition-colors cursor-pointer" onClick={() => setSelectedTicket(t)}>
              <div className="flex items-start justify-between mb-1">
                <div className="text-[12px] font-700 text-primary">{t.id}</div>
                <StatusBadge status={t.status} />
              </div>
              <div className="text-[12px] font-600 text-foreground mb-1">{t.title}</div>
              <div className="text-[11px] text-muted-foreground">{t.agency}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}